import { apiClient } from './apiClient';
import { parseEntryIds } from './timesheetParsers';
import type { Timesheet } from '@/types/timesheet';
import type { ApprovingForApprover } from '@/components/ApprovalDashboard/ApprovingForSwitcher';
import type { TimesheetWorkflowStatus, TimesheetWorkflowHistory, StageHistory, ApprovalResponseHistory, WorkflowEventHistory, PendingApprovalsCount, SubmitTimesheetResponse, ApproveTimesheetResponse, RejectTimesheetResponse, ConfirmSubmitResponse } from '@/types/workflow-status';

/**
 * Parse date fields in a timesheet object from API response.
 */
function parseTimesheetDates(timesheet: any): Timesheet {
  return {
    ...timesheet,
    entries: Array.isArray(timesheet.entries)
      ? timesheet.entries.map(parseEntryIds)
      : timesheet.entries,
    weekEnding: timesheet.weekEnding ? new Date(timesheet.weekEnding) : timesheet.weekEnding,
    submittedAt: timesheet.submittedAt ? new Date(timesheet.submittedAt) : timesheet.submittedAt,
    approvedAt: timesheet.approvedAt ? new Date(timesheet.approvedAt) : timesheet.approvedAt,
    rejectedAt: timesheet.rejectedAt ? new Date(timesheet.rejectedAt) : timesheet.rejectedAt,
    recalledAt: timesheet.recalledAt ? new Date(timesheet.recalledAt) : timesheet.recalledAt,
  };
}

/**
 * Parse date fields in a workflow status object from API response.
 */
function parseWorkflowStatusDates(status: any): TimesheetWorkflowStatus {
  return {
    ...status,
    startedAt: status.startedAt ? new Date(status.startedAt) : status.startedAt,
    completedAt: status.completedAt ? new Date(status.completedAt) : status.completedAt,
  };
}

/**
 * Parse date fields in an approval response object from API response.
 */
function parseApprovalResponseDates(response: any): ApprovalResponseHistory {
  return {
    ...response,
    respondedAt: response.respondedAt ? new Date(response.respondedAt) : response.respondedAt,
  };
}

/**
 * Parse date fields in a stage history object from API response.
 */
function parseStageHistoryDates(stage: any): StageHistory {
  return {
    ...stage,
    startedAt: stage.startedAt ? new Date(stage.startedAt) : stage.startedAt,
    completedAt: stage.completedAt ? new Date(stage.completedAt) : stage.completedAt,
    responses: (stage.responses || []).map(parseApprovalResponseDates),
  };
}

/**
 * Parse date fields in a workflow event object from API response.
 */
function parseWorkflowEventDates(event: any): WorkflowEventHistory {
  return {
    ...event,
    timestamp: event.timestamp ? new Date(event.timestamp) : event.timestamp,
  };
}

/**
 * Parse date fields in a workflow history object from API response.
 */
export function parseWorkflowHistoryDates(history: any): TimesheetWorkflowHistory {
  return {
    ...history,
    startedAt: history.startedAt ? new Date(history.startedAt) : history.startedAt,
    completedAt: history.completedAt ? new Date(history.completedAt) : history.completedAt,
    stages: (history.stages || []).map(parseStageHistoryDates),
    events: (history.events || []).map(parseWorkflowEventDates),
  };
}

/**
 * API service for approval-related operations.
 * Provides methods for fetching pending approvals, approving/rejecting timesheets,
 * and retrieving workflow status and history.
 */
export const approvalsApi = {
  /**
   * Get pending approvals for the current user.
   * Uses server-side filtering based on workflow assignments and delegation.
   * When delegatedFromManagerIds is provided, the server also includes
   * timesheets assigned to those managers (active delegation routing).
   */
  async getPendingApprovals(delegatedFromManagerIds?: string[]): Promise<Timesheet[]> {
    const params = new URLSearchParams();
    if (delegatedFromManagerIds?.length) {
      delegatedFromManagerIds.forEach(id => params.append('delegatedFromManagerId', id));
    }
    const qs = params.toString();
    const timesheets = await apiClient.get<any[]>(`/approvals/pending${qs ? `?${qs}` : ''}`);
    return timesheets.map(parseTimesheetDates);
  },

  /**
   * Get the count breakdown for pending approvals.
   * Returns total count, workflow-based count, and legacy count.
   * When delegatedFromManagerIds is provided, the count includes
   * timesheets assigned to those managers (active delegation routing).
   */
  async getPendingApprovalsCount(delegatedFromManagerIds?: string[]): Promise<PendingApprovalsCount> {
    const params = new URLSearchParams();
    if (delegatedFromManagerIds?.length) {
      delegatedFromManagerIds.forEach(id => params.append('delegatedFromManagerId', id));
    }
    const qs = params.toString();
    return apiClient.get<PendingApprovalsCount>(`/approvals/pending/count${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get the approvers whose queues the current user may approve from
   * (Approve on Behalf of Others). Empty when the feature is disabled.
   */
  async getApprovableFor(): Promise<ApprovingForApprover[]> {
    const approvers = await apiClient.get<{ id: number; name: string }[]>('/approvals/approvable-for');
    return approvers.map(a => ({ id: String(a.id), name: a.name }));
  },

  /**
   * Get the current workflow status for a timesheet.
   */
  async getWorkflowStatus(timesheetId: number): Promise<TimesheetWorkflowStatus> {
    const status = await apiClient.get<any>(`/timesheets/${timesheetId}/workflow`);
    return parseWorkflowStatusDates(status);
  },

  /**
   * Get the full workflow history for a timesheet.
   * Includes all stages and approval responses.
   */
  async getWorkflowHistory(timesheetId: number): Promise<TimesheetWorkflowHistory> {
    const history = await apiClient.get<any>(`/timesheets/${timesheetId}/workflow/history`);
    return parseWorkflowHistoryDates(history);
  },

  /**
   * Submit a timesheet for approval.
   * This will start the workflow if one is assigned to the employee.
   */
  async submitTimesheet(timesheetId: number): Promise<SubmitTimesheetResponse> {
    return apiClient.post<SubmitTimesheetResponse, object>(`/timesheets/${timesheetId}/submit`, {});
  },

  /**
   * Approve a timesheet.
   * For workflow-based approvals, this advances to the next stage or completes the workflow.
   * For legacy approvals, this directly approves the timesheet.
   */
  async approveTimesheet(
    timesheetId: number,
    notes?: string,
    confirmed?: boolean,
    queueOwnerId?: number
  ): Promise<ApproveTimesheetResponse> {
    const body: { notes?: string; confirmed?: boolean; queueOwnerId?: number } = { notes };
    // Only signal confirmation once the approver has accepted a post-approval rule
    // message, so the standard one-click approve path keeps its original payload.
    if (confirmed) {
      body.confirmed = true;
    }
    // Queue owner is context for the server's Approve on Behalf of Others check.
    if (queueOwnerId !== undefined) {
      body.queueOwnerId = queueOwnerId;
    }
    return apiClient.post<ApproveTimesheetResponse, { notes?: string; confirmed?: boolean; queueOwnerId?: number }>(
      `/timesheets/${timesheetId}/approve`,
      body
    );
  },

  /**
   * Reject a timesheet.
   * This rejects the timesheet and records the rejection reason.
   */
  async rejectTimesheet(timesheetId: number, reason: string): Promise<RejectTimesheetResponse> {
    return apiClient.post<RejectTimesheetResponse, { reason: string }>(`/timesheets/${timesheetId}/reject`, { reason });
  },

  /**
   * Record an approver edit audit event for a timesheet.
   * Called after an approver saves edits to timesheet entries during the review process.
   */
  async recordApproverEdit(timesheetId: number, request: {
    comment?: string;
    changes?: Array<{
      entryId?: number;
      field: string;
      oldValue?: string;
      newValue?: string;
    }>;
  }): Promise<{ message: string }> {
    return apiClient.post<{ message: string }, typeof request>(`/timesheets/${timesheetId}/approver-edit`, request);
  },

  /**
   * Confirm or decline a pre-stage rule message after initial submit returned
   * requiresConfirmation=true.
   */
  async confirmSubmission(timesheetId: number, accepted: boolean): Promise<ConfirmSubmitResponse> {
    return apiClient.post<ConfirmSubmitResponse, { accepted: boolean }>(
      `/timesheets/${timesheetId}/confirm-submit`,
      { accepted }
    );
  },
};
